import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router';
import { identify, resetIdentity, trackPageView } from './lib/analytics';
import {
  Building2, Calendar, DollarSign, Gavel, LayoutDashboard, LogOut,
  ShieldCheck, User, Undo2, Users as UsersIcon,
  Settings as SettingsIcon, Scroll, Ticket, Crown, Inbox as InboxIcon, Activity,
} from 'lucide-react';
import supabase from './lib/supabase';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clubs from './pages/Clubs';
import ClubDetail from './pages/ClubDetail';
import Coaches from './pages/Coaches';
import CoachDetail from './pages/CoachDetail';
import CoachEdit from './pages/CoachEdit';
import Payouts from './pages/Payouts';
import Refunds from './pages/Refunds';
import Verifications from './pages/Verifications';
import Inbox from './pages/Inbox';
import Operations from './pages/Operations';
import Users from './pages/Users';
import Bookings from './pages/Bookings';
import Settings from './pages/Settings';
import Disputes from './pages/Disputes';
import AuditLog from './pages/AuditLog';
import Promos from './pages/Promos';
import Account from './pages/Account';

type Session = Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'];

type Tier = 'super_admin' | 'club_admin' | 'none';

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [tier, setTier] = useState<Tier | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session) {
        identify(data.session.user.id, { email: data.session.user.email });
        await checkRole(data.session.user.id);
      }
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, sess) => {
      setSession(sess);
      if (sess) {
        identify(sess.user.id, { email: sess.user.email });
        await checkRole(sess.user.id);
      } else {
        resetIdentity();
        setTier(null);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const checkRole = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('is_super_admin, is_club_admin, is_admin')
      .eq('user_id', userId)
      .single();
    // is_super_admin takes precedence; fall back to is_club_admin; the
    // legacy is_admin flag still qualifies as super for pre-split envs.
    if (data?.is_super_admin || data?.is_admin) setTier('super_admin');
    else if (data?.is_club_admin) setTier('club_admin');
    else setTier('none');
  };

  if (loading) return <div className="p-10 text-sm text-gray-500">Loading…</div>;
  if (!session) return <Login />;
  if (tier === 'none') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-xl mb-2">Not authorized</h1>
          <p className="text-sm text-gray-600 mb-4">
            This account does not have admin access.
          </p>
          <button
            onClick={() => supabase.auth.signOut()}
            className="h-10 px-5 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const isSuper = tier === 'super_admin';

  return (
    <BrowserRouter>
      <div className="min-h-screen grid" style={{ gridTemplateColumns: '240px 1fr' }}>
        <aside className="bg-[#1a3010] text-white flex flex-col p-4 gap-1">
          <div className="px-3 py-4 flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#c6f806] flex items-center justify-center">
              <span className="text-[#1a3010] font-bold">A</span>
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">AceRent</div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Admin</div>
            </div>
          </div>

          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" end />
          <NavItem to="/inbox" icon={InboxIcon} label="Inbox" />
          <NavItem to="/bookings" icon={Calendar} label="Bookings" />
          <NavItem to="/clubs" icon={Building2} label="Clubs & Courts" />
          <NavItem to="/coaches" icon={User} label="Coaches" />
          <NavItem to="/users" icon={UsersIcon} label="Users" />
          <NavItem to="/verifications" icon={ShieldCheck} label="Verifications" />
          <NavItem to="/account" icon={User} label="Account" />

          {/* Super-admin only: money + platform controls */}
          {isSuper && (
            <>
              <div className="px-3 mt-3 mb-1 text-[10px] uppercase tracking-widest text-white/40">
                Platform
              </div>
              <NavItem to="/payouts" icon={DollarSign} label="Payouts" />
              <NavItem to="/refunds" icon={Undo2} label="Refunds" />
              <NavItem to="/disputes" icon={Gavel} label="Disputes" />
              <NavItem to="/operations" icon={Activity} label="Operations" />
              <NavItem to="/promos" icon={Ticket} label="Promo codes" />
              <NavItem to="/audit" icon={Scroll} label="Audit log" />
              <NavItem to="/settings" icon={SettingsIcon} label="Settings" />
            </>
          )}

          <div className="mt-auto px-3 py-2 border-t border-white/10">
            <div className="flex items-center gap-1.5 text-[10px] text-white/70 uppercase tracking-wider mb-1">
              {isSuper
                ? <><Crown className="w-3 h-3 text-[#c6f806]" /> Super admin</>
                : <><ShieldCheck className="w-3 h-3 text-white/50" /> Club admin</>}
            </div>
            <div className="text-xs text-white/60 truncate">{session.user.email}</div>
          </div>
          <button
            onClick={() => supabase.auth.signOut()}
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-white/80 hover:bg-white/10"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </aside>
        <main className="min-h-screen bg-[#f6f7f4]">
          <RouteTracker />
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/bookings" element={<Bookings />} />
            <Route path="/clubs" element={<Clubs />} />
            <Route path="/clubs/:id" element={<ClubDetail />} />
            <Route path="/coaches" element={<Coaches />} />
            <Route path="/coaches/:id" element={<CoachDetail />} />
            <Route path="/coaches/:id/edit" element={<CoachEdit />} />
            <Route path="/users" element={<Users tier={tier} />} />
            <Route path="/verifications" element={<Verifications />} />
            <Route path="/account" element={<Account />} />
            {/* Super-admin-only routes are physically registered only when
                isSuper — a club_admin who types the URL gets a 404 redirect.
                The real authZ gate is RLS, which refuses the data fetch;
                this is just the UX layer. */}
            {isSuper && (
              <>
                <Route path="/payouts" element={<Payouts />} />
                <Route path="/refunds" element={<Refunds />} />
                <Route path="/disputes" element={<Disputes />} />
                <Route path="/operations" element={<Operations />} />
                <Route path="/promos" element={<Promos />} />
                <Route path="/audit" element={<AuditLog />} />
                <Route path="/settings" element={<Settings />} />
              </>
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

/**
 * Fires a PostHog $pageview every time the route changes. Screen
 * name = the path's first two segments so URLs with dynamic ids
 * (e.g. /clubs/:id) collapse into stable bucket labels.
 */
function RouteTracker() {
  const loc = useLocation();
  useEffect(() => {
    const parts = loc.pathname.split('/').filter(Boolean);
    const screen = parts.length === 0 ? 'root' : parts.slice(0, 2).join('/');
    trackPageView(screen, { path: loc.pathname });
  }, [loc.pathname]);
  return null;
}

function NavItem({
  to, icon: Icon, label, end,
}: { to: string; icon: any; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors ${
          isActive ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10'
        }`
      }
    >
      <Icon className="w-4 h-4" />
      {label}
    </NavLink>
  );
}
